import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          service: 'Lavage résidentiel',
        }),
      });

      if (response.ok) {
        toast({
          title: 'Message envoyé!',
          description: 'Nous vous contacterons dans les plus brefs délais.',
        });
        setFormData({ name: '', address: '', phone: '', email: '' });
      } else {
        throw new Error('Erreur');
      }
    } catch {
      toast({
        title: 'Erreur',
        description: 'Veuillez réessayer ou nous appeler au 819.200.2015',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-10 md:py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-700 rounded-full px-4 py-2 mb-4">
            <Send size={18} />
            <span className="text-sm font-semibold">Contactez-nous</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Obtenez votre{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-pink-500 bg-clip-text text-transparent">
              estimation gratuite
            </span>
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            Remplissez le formulaire et nous vous contacterons rapidement.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 md:gap-12 max-w-6xl mx-auto">
          <Card className="lg:col-span-3 p-6 md:p-10 border-0 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Nom *
                </label>
                <Input
                  data-testid="contact-form-name"
                  id="contact-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Tremblay"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <label htmlFor="contact-address" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Adresse *
                </label>
                <Input
                  data-testid="contact-form-address"
                  id="contact-address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Rue Principale, Centre du Québec"
                  className="h-12 text-base"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Téléphone *
                  </label>
                  <Input
                    data-testid="contact-form-phone"
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(819) 555-0100"
                    className="h-12 text-base"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Courriel *
                  </label>
                  <Input
                    data-testid="contact-form-email"
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jean@exemple.com"
                    className="h-12 text-base"
                  />
                </div>
              </div>

              <Button
                data-testid="contact-form-submit"
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl transition-all transform hover:scale-105"
              >
                {isSubmitting ? 'Envoi en cours...' : (
                  <>
                    <Send className="mr-2" size={20} />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            </form>
          </Card>

          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            <Card className="p-5 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full p-3 flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Téléphone</h3>
                  <a href="tel:8192002015" className="text-cyan-600 hover:text-cyan-700 font-semibold text-lg">
                    819.200.2015
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Disponible 7 jours sur 7</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full p-3 flex-shrink-0">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Courriel</h3>
                  <a href="mailto:groupelavagefenetre@gmail.com" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                    groupelavagefenetre@gmail.com
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Réponse sous 24h</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 border-0 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-br from-cyan-500 to-pink-500 rounded-full p-3 flex-shrink-0">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Secteur</h3>
                  <p className="text-gray-700 font-medium">Partout au Québec</p>
                  <p className="text-sm text-gray-500 mt-1">Centre du Québec et environs</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
